import React, { useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  FormProvider as ReactHookFormProvider,
  useForm,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import useGetCompanyVerificationInfo from '../../../common/hooks/useGetCompanyVerificationInfo';
import { useGetSessionCompany } from '../../../common/hooks/useGetSessionCompany';
import {
  BusinessVerificationLocationType,
  RejectionReason,
  VerificationMethod,
} from '../../../common/interfaces/entities';
import { User } from '../../../models/User';
import { getUser } from '../../../selectors/user';
import { useCompanyVerificationModalsContext } from '../../CompanyVerificationModals/CompanyVerificationModalsProvider';
import useGetCompanyVerificationMethods from './hooks/useGetCompanyVerificationMethods';
import {
  CompanyVerificationFormState,
  VerifyWithProofPersonalDocumentType,
} from './types';
import { companyVerificationFormValidator } from './validation';

type Props = {
  children: React.ReactNode;
};
export default function FormProvider({ children }: Props) {
  const intl = useIntl();
  const { sessionCompany } = useGetSessionCompany();
  const { companyVerificationMethods } = useGetCompanyVerificationMethods();
  const { verificationInfo } = useGetCompanyVerificationInfo();
  const { preSelectedVerificationMethod: contextPreSelectedMethod } =
    useCompanyVerificationModalsContext();
  const user: User = useSelector(getUser);
  const prefilledFormValues = useMemo<
    Partial<CompanyVerificationFormState>
  >(() => {
    const lastVerificationRequest = verificationInfo?.lastVerificationRequest;
    if (!lastVerificationRequest) {
      return {
        verificationMethod:
          contextPreSelectedMethod ??
          companyVerificationMethods[0].verificationMethod,
        verifyWithLegalDocuments: {
          companyName: sessionCompany?.name || '',
        },
        verifyWithProofOfOwnership: {
          personalDocumentType: VerifyWithProofPersonalDocumentType.NPWP,
          businessLocationType: BusinessVerificationLocationType.ONLINE,
        },
      } as Partial<CompanyVerificationFormState>;
    }

    const { document, companyVerificationMethod, rejectionReasons } =
      lastVerificationRequest;
    const verificationInProgress =
      verificationInfo?.isInitiated && !verificationInfo?.isRejected;
    const tierUpgradeInProgress =
      verificationInfo.tierUpgrade?.isUpgradeInitiated &&
      !verificationInfo.tierUpgrade?.isUpgradeRejected;

    const isViewOnly = verificationInProgress || tierUpgradeInProgress;

    const resolvePreSelectedVerificationMethod = (): VerificationMethod => {
      if (isViewOnly) {
        return companyVerificationMethod.verificationMethod;
      }
      if (contextPreSelectedMethod) {
        return contextPreSelectedMethod;
      }
      const tierUpgradeMethod =
        verificationInfo?.tierUpgrade?.availableTierUpgrade[0]
          ?.verificationMethod;
      return tierUpgradeMethod ?? companyVerificationMethod.verificationMethod;
    };
    const preSelectedVerificationMethod =
      resolvePreSelectedVerificationMethod();

    const hasCompanyNameMismatch = Boolean(
      rejectionReasons?.includes(
        RejectionReason.AUTO_LEGAL_DOCUMENT_COMPANY_NAME_MISMATCH
      )
    );

    return {
      verificationMethod: preSelectedVerificationMethod,
      isViewOnly: isViewOnly,
      ...(document.verificationMethod === VerificationMethod.LEGAL_DOCUMENTS
        ? ({
            verifyWithLegalDocuments: {
              companyNIBNumber: document.companyNibNumber || '',
              companyNPWPDoc: document.companyNpwpDocument || null,
              companyNPWPNumber: document.companyNpwpNumber || null,
              companyNPWPDocMetadata: document.companyNpwpDocumentMetadata,
              additionalDocuments: document.additionalDocuments || [],
              acraBizFile: document.acraBizFile || null,
              companyUniqueEntityNumber:
                document.companyUniqueEntityNumber || null,
              companyLegalDoc: document.companyLegalDoc || null,
              companyLegalNumber: document.companyLegalNumber || null,
              allowCompanyNameEdit: hasCompanyNameMismatch,
              companyName: sessionCompany?.name || '',
              companyExistsError: undefined,
            },
            verifyWithProofOfOwnership: {
              personalDocumentType: VerifyWithProofPersonalDocumentType.NPWP,
              businessLocationType: BusinessVerificationLocationType.ONLINE,
              additionalDocuments: document.additionalDocuments || [],
            },
          } as Partial<CompanyVerificationFormState>)
        : {
            verifyWithProofOfOwnership: {
              businessActivityImages: document.businessImages,
              personalNIBNumber: document.personalNibNumber || '',
              personalNPWPDoc: document.personalNpwpDocument || '',
              personalNPWPNumber: document.personalNpwpNumber || '',
              personalDocumentType: document.ktpDoc
                ? VerifyWithProofPersonalDocumentType.KTP
                : VerifyWithProofPersonalDocumentType.NPWP,
              isWhatsAppVerified: Boolean(user.isWhatsappVerified),
              ktpDoc: document.ktpDoc || '',
              ktpNumber: document.ktpNumber || '',
              businessLocationType: document.businessLocationType,
              additionalDocuments: document.additionalDocuments || [],
            },
          }),
    };
  }, [
    companyVerificationMethods,
    contextPreSelectedMethod,
    user.isWhatsappVerified,
    verificationInfo?.isInitiated,
    verificationInfo?.isRejected,
    verificationInfo?.lastVerificationRequest,
    verificationInfo?.tierUpgrade?.availableTierUpgrade,
    verificationInfo?.tierUpgrade?.isUpgradeInitiated,
    verificationInfo?.tierUpgrade?.isUpgradeRejected,
    sessionCompany,
  ]);
  const methods = useForm<CompanyVerificationFormState>({
    mode: 'all',
    resolver: yupResolver(
      companyVerificationFormValidator(intl, sessionCompany?.CountryCode)
    ),
    defaultValues: prefilledFormValues,
  });
  return <ReactHookFormProvider {...methods}>{children}</ReactHookFormProvider>;
}
